import { useEffect, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader } from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "../components/ui/command";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Calculator, TrendingUp, Award, Search, ShoppingCart } from "lucide-react";

const fmt = (n) => `€ ${(Number(n) || 0).toFixed(2)}`;
const emptyIng = () => ({ name: "", quantity: 1, unit: "kg", unit_cost: 0 });
const empty = () => ({ name: "", portions: 1, selling_price: 0, sold: 0, ingredients: [emptyIng()], notes: "" });

const totalCost = (f) => (f.ingredients || []).reduce((s, i) => s + (parseFloat(i.quantity) || 0) * (parseFloat(i.unit_cost) || 0), 0);
const perPortion = (f) => totalCost(f) / (parseFloat(f.portions) || 1);
const fcPct = (f) => { const p = parseFloat(f.selling_price) || 0; return p > 0 ? (perPortion(f) / p) * 100 : 0; };
const pctColor = (p) => p === 0 ? "text-muted-foreground" : p < 30 ? "text-[hsl(var(--success))]" : p <= 40 ? "text-[hsl(var(--warning))]" : "text-destructive";
const marginOf = (f) => (parseFloat(f.selling_price) || 0) - perPortion(f);
const ME = {
  star: { label: "Stella", desc: "Molto venduto e alto margine — spingilo e proteggilo", cls: "bg-amber-500/15 text-amber-600 dark:text-amber-400" },
  plow: { label: "Cavallo", desc: "Molto venduto ma basso margine — riduci il costo o ritocca il prezzo", cls: "bg-sky-500/15 text-sky-600 dark:text-sky-400" },
  puzzle: { label: "Enigma", desc: "Poco venduto ma alto margine — promuovilo in menù", cls: "bg-violet-500/15 text-violet-600 dark:text-violet-400" },
  dog: { label: "Cane", desc: "Poco venduto e basso margine — valuta di toglierlo", cls: "bg-rose-500/15 text-rose-600 dark:text-rose-400" },
};

export default function FoodCost() {
  const [items, setItems] = useState([]);
  const [invNames, setInvNames] = useState([]);
  const [prices, setPrices] = useState({});
  const [alerts, setAlerts] = useState([]);
  const [pstats, setPstats] = useState([]);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(empty());
  const [searchOpen, setSearchOpen] = useState(false);

  const load = async () => setItems((await api.get("/food-costs")).data);
  useEffect(() => {
    load();
    api.get("/inventory").then(({ data }) => setInvNames(data.map((i) => i.name))).catch(() => {});
    api.get("/ingredient-prices").then(({ data }) => {
      const map = {};
      data.forEach((p) => { map[(p.name || "").toLowerCase().trim()] = p; });
      setPrices(map);
    }).catch(() => {});
    api.get("/price-alerts").then(({ data }) => setAlerts(data)).catch(() => {});
    api.get("/purchase-stats").then(({ data }) => setPstats(data)).catch(() => {});
  }, []);

  const priceFor = (name) => prices[(name || "").toLowerCase().trim()];
  const openNew = () => { setEditId(null); setForm(empty()); setOpen(true); };
  const openEdit = (f) => { setEditId(f.id); setForm({ ...f, ingredients: f.ingredients?.length ? f.ingredients : [emptyIng()] }); setOpen(true); };

  const updIng = (idx, k, v) => setForm((f) => ({ ...f, ingredients: f.ingredients.map((it, i) => {
    if (i !== idx) return it;
    const next = { ...it, [k]: v };
    if (k === "name") { const p = priceFor(v); if (p) { next.unit_cost = p.current_cost; if (p.unit) next.unit = p.unit; } }
    return next;
  }) }));
  const addIng = () => setForm((f) => ({ ...f, ingredients: [...f.ingredients, emptyIng()] }));
  const delIng = (idx) => setForm((f) => ({ ...f, ingredients: f.ingredients.filter((_, i) => i !== idx) }));

  const ingredientOptions = (() => {
    const map = new Map();
    Object.values(prices).forEach((p) => { const k = (p.name || "").toLowerCase().trim(); if (k) map.set(k, { name: p.name, unit: p.unit || "", cost: p.current_cost || 0 }); });
    invNames.forEach((n) => { const k = (n || "").toLowerCase().trim(); if (k && !map.has(k)) map.set(k, { name: n, unit: "", cost: 0 }); });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();
  const addFromSearch = (opt) => {
    setForm((f) => ({ ...f, ingredients: [...f.ingredients.filter((it) => it.name), { name: opt.name, quantity: 1, unit: opt.unit || "kg", unit_cost: opt.cost || 0 }] }));
    setSearchOpen(false);
  };

  const save = async () => {
    if (!form.name.trim()) return toast.error("Inserisci il nome del piatto");
    const payload = { ...form, portions: parseFloat(form.portions) || 1, selling_price: parseFloat(form.selling_price) || 0,
      ingredients: form.ingredients.filter((i) => i.name).map((i) => ({ ...i, quantity: parseFloat(i.quantity) || 0, unit_cost: parseFloat(i.unit_cost) || 0 })) };
    try {
      if (editId) await api.put(`/food-costs/${editId}`, payload);
      else await api.post("/food-costs", payload);
      toast.success("Ricetta salvata"); setOpen(false); setEditId(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const remove = async (id) => { await api.delete(`/food-costs/${id}`); load(); };

  const suggested = perPortion(form) > 0 ? perPortion(form) / 0.30 : 0;

  const priced = items.filter((f) => (parseFloat(f.selling_price) || 0) > 0);
  const avgSold = priced.length ? priced.reduce((s, f) => s + (parseFloat(f.sold) || 0), 0) / priced.length : 0;
  const avgMargin = priced.length ? priced.reduce((s, f) => s + marginOf(f), 0) / priced.length : 0;
  const classOf = (f) => {
    if ((parseFloat(f.selling_price) || 0) <= 0) return null;
    const hiPop = (parseFloat(f.sold) || 0) >= avgSold;
    const hiMar = marginOf(f) >= avgMargin;
    return hiPop ? (hiMar ? "star" : "plow") : (hiMar ? "puzzle" : "dog");
  };
  const meCounts = priced.reduce((acc, f) => { const k = classOf(f); acc[k] = (acc[k] || 0) + 1; return acc; }, {});

  return (
    <div>
      <PageHeader title="Food Cost" subtitle="Costo delle ricette, margini e prezzo di vendita consigliato" testid="foodcost-header" />
      <div className="flex justify-end mb-4">
        <Button onClick={openNew} className="rounded-full" data-testid="add-foodcost-button"><Plus className="w-4 h-4 mr-1" /> Nuova ricetta</Button>
      </div>

      {alerts.length > 0 && (
        <Card className="p-5 rounded-xl border-destructive/40 mb-6" data-testid="price-alerts">
          <div className="flex items-center gap-2 mb-3 text-destructive"><TrendingUp className="w-5 h-5" /><h3 className="font-dash font-semibold">Aumenti prezzo in acquisto</h3></div>
          <div className="space-y-2">
            {alerts.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-sm border-b border-border pb-2 last:border-0" data-testid={`alert-${a.id}`}>
                <div><span className="font-medium">{a.name}</span> <span className="text-muted-foreground">· {a.supplier || "—"}</span></div>
                <div className="text-right">
                  <span className="text-destructive font-semibold">+{a.pct_change}%</span>
                  <span className="text-muted-foreground ml-2">{fmt(a.previous_cost)} → {fmt(a.current_cost)}/{a.unit || "u"}</span>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {pstats.length > 0 && (
        <Card className="p-5 rounded-xl mb-6" data-testid="purchase-stats">
          <div className="flex items-center gap-2 mb-1"><ShoppingCart className="w-5 h-5 text-primary" /><h3 className="font-dash font-semibold">Statistiche acquisti</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Prodotti consolidati tra tutte le fatture: quantità totale acquistata, spesa e prezzo medio ponderato.</p>
          <div className="overflow-x-auto -mx-1">
            <table className="w-full text-sm">
              <thead><tr className="text-left text-xs uppercase text-muted-foreground border-b border-border">
                <th className="py-2 px-1">Prodotto</th><th className="py-2 px-1 text-right">Q.tà tot.</th><th className="py-2 px-1 text-right">Spesa tot.</th><th className="py-2 px-1 text-right">€ medio</th><th className="py-2 px-1 text-right">Acquisti</th><th className="py-2 px-1">Fornitore</th>
              </tr></thead>
              <tbody>
                {pstats.slice(0, 15).map((p, i) => (
                  <tr key={i} className="border-b border-border/60 last:border-0" data-testid={`pstat-row-${i}`}>
                    <td className="py-2 px-1 font-medium">{p.name}</td>
                    <td className="py-2 px-1 text-right">{Number(p.total_qty).toFixed(2)} {p.unit}</td>
                    <td className="py-2 px-1 text-right font-semibold">{fmt(p.total_spend)}</td>
                    <td className="py-2 px-1 text-right">{fmt(p.avg_cost)}{p.unit ? `/${p.unit}` : ""}</td>
                    <td className="py-2 px-1 text-right text-muted-foreground">{p.purchase_count}×</td>
                    <td className="py-2 px-1 text-muted-foreground">{p.supplier || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {priced.length > 0 && (
        <Card className="p-5 rounded-xl mb-6" data-testid="menu-engineering">
          <div className="flex items-center gap-2 mb-1"><Award className="w-5 h-5 text-primary" /><h3 className="font-dash font-semibold">Menu Engineering</h3></div>
          <p className="text-xs text-muted-foreground mb-4">Classificazione dei piatti per popolarità (venduti) e margine, rispetto alla media. Inserisci i "venduti" in ogni ricetta per aggiornarla.</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            {["star", "plow", "puzzle", "dog"].map((k) => (
              <div key={k} className={`rounded-xl p-4 ${ME[k].cls}`} data-testid={`me-count-${k}`}>
                <div className="flex items-baseline justify-between"><span className="font-semibold">{ME[k].label}</span><span className="font-dash text-2xl font-bold">{meCounts[k] || 0}</span></div>
                <p className="text-xs mt-1 opacity-90">{ME[k].desc}</p>
              </div>
            ))}
          </div>
        </Card>
      )}

      {items.length === 0 ? (
        <Card className="p-10 text-center text-muted-foreground rounded-xl" data-testid="foodcost-empty">
          <Calculator className="w-10 h-10 mx-auto mb-3 text-primary/60" />
          Nessuna ricetta. Crea la prima per calcolare il food cost.
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((f) => {
            const p = fcPct(f);
            const mk = classOf(f);
            return (
              <Card key={f.id} className="p-5 rounded-xl" data-testid={`foodcost-card-${f.id}`}>
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-dash font-semibold text-lg">{f.name}</h3>
                    {mk && <span className={`text-xs px-2 py-0.5 rounded-full ${ME[mk].cls}`} data-testid={`me-badge-${f.id}`}>{ME[mk].label}</span>}
                  </div>
                  <div className="flex">
                    <Button size="icon" variant="ghost" onClick={() => openEdit(f)} data-testid={`edit-foodcost-${f.id}`}><Pencil className="w-4 h-4 text-primary" /></Button>
                    <Button size="icon" variant="ghost" onClick={() => remove(f.id)} data-testid={`delete-foodcost-${f.id}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-y-2 text-sm">
                  <span className="text-muted-foreground">Costo/porzione</span><span className="text-right font-medium">{fmt(perPortion(f))}</span>
                  <span className="text-muted-foreground">Prezzo vendita</span><span className="text-right font-medium">{fmt(f.selling_price)}</span>
                  <span className="text-muted-foreground">Food cost</span><span className={`text-right font-semibold ${pctColor(p)}`}>{p.toFixed(1)}%</span>
                  <span className="text-muted-foreground">Margine</span><span className="text-right font-medium">{fmt((parseFloat(f.selling_price) || 0) - perPortion(f))}</span>
                  <span className="text-muted-foreground">Venduti</span><span className="text-right font-medium">{parseFloat(f.sold) || 0}</span>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setEditId(null); }}>
        <DialogContent className="max-w-2xl" data-testid="foodcost-dialog">
          <DialogHeader><DialogTitle>{editId ? "Modifica ricetta" : "Nuova ricetta"}</DialogTitle></DialogHeader>
          <div className="space-y-4 max-h-[65vh] overflow-y-auto pr-1">
            <datalist id="inv-names">{invNames.map((n) => <option key={n} value={n} />)}</datalist>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="space-y-1.5 col-span-2 sm:col-span-1"><Label>Piatto</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="fc-name-input" /></div>
              <div className="space-y-1.5"><Label>Porzioni</Label><Input type="number" value={form.portions} onChange={(e) => setForm({ ...form, portions: e.target.value })} data-testid="fc-portions-input" /></div>
              <div className="space-y-1.5"><Label>Prezzo vendita €</Label><Input type="number" value={form.selling_price} onChange={(e) => setForm({ ...form, selling_price: e.target.value })} data-testid="fc-price-input" /></div>
              <div className="space-y-1.5"><Label>Venduti (periodo)</Label><Input type="number" value={form.sold} onChange={(e) => setForm({ ...form, sold: e.target.value })} data-testid="fc-sold-input" /></div>
            </div>

            <div className="space-y-2">
              <div className="flex items-center justify-between flex-wrap gap-2"><Label>Ingredienti</Label>
                <div className="flex items-center gap-2">
                  <Popover open={searchOpen} onOpenChange={setSearchOpen}>
                    <PopoverTrigger asChild>
                      <Button type="button" size="sm" variant="outline" className="rounded-full h-8 text-xs" data-testid="fc-search-ingredient"><Search className="w-3.5 h-3.5 mr-1" /> Cerca ingrediente</Button>
                    </PopoverTrigger>
                    <PopoverContent className="p-0 w-[320px]" align="end">
                      <Command>
                        <CommandInput placeholder="Cerca tra magazzino e prezzi…" data-testid="fc-ing-search-input" />
                        <CommandList>
                          <CommandEmpty>Nessun ingrediente trovato</CommandEmpty>
                          <CommandGroup>
                            {ingredientOptions.slice(0, 500).map((opt) => (
                              <CommandItem key={opt.name} value={opt.name} onSelect={() => addFromSearch(opt)} data-testid={`fc-ing-opt-${opt.name}`}>
                                <div className="flex flex-col"><span className="text-sm">{opt.name}</span>{opt.cost > 0 && <span className="text-xs text-muted-foreground">{fmt(opt.cost)}/{opt.unit || "u"}</span>}</div>
                              </CommandItem>
                            ))}
                          </CommandGroup>
                        </CommandList>
                      </Command>
                    </PopoverContent>
                  </Popover>
                  <Button size="sm" variant="outline" className="rounded-full h-8" onClick={addIng} data-testid="fc-add-ingredient"><Plus className="w-3.5 h-3.5 mr-1" /> Aggiungi</Button>
                </div>
              </div>
              {form.ingredients.map((it, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center">
                  <Input className="col-span-5 h-8" list="inv-names" placeholder="Ingrediente" value={it.name} onChange={(e) => updIng(i, "name", e.target.value)} data-testid={`fc-ing-name-${i}`} />
                  <Input className="col-span-2 h-8" type="number" placeholder="Qtà" value={it.quantity} onChange={(e) => updIng(i, "quantity", e.target.value)} data-testid={`fc-ing-qty-${i}`} />
                  <Input className="col-span-2 h-8" placeholder="Unità" value={it.unit} onChange={(e) => updIng(i, "unit", e.target.value)} data-testid={`fc-ing-unit-${i}`} />
                  <Input className="col-span-2 h-8" type="number" placeholder="€/unità" value={it.unit_cost} onChange={(e) => updIng(i, "unit_cost", e.target.value)} data-testid={`fc-ing-cost-${i}`} />
                  <Button size="icon" variant="ghost" className="col-span-1 h-8 w-8" onClick={() => delIng(i)} data-testid={`fc-ing-del-${i}`}><Trash2 className="w-4 h-4 text-destructive" /></Button>
                </div>
              ))}
            </div>

            <div className="rounded-xl bg-secondary p-4 grid grid-cols-2 gap-y-1 text-sm" data-testid="fc-summary">
              <span className="text-muted-foreground">Costo totale</span><span className="text-right font-medium">{fmt(totalCost(form))}</span>
              <span className="text-muted-foreground">Costo per porzione</span><span className="text-right font-medium">{fmt(perPortion(form))}</span>
              <span className="text-muted-foreground">Food cost</span><span className={`text-right font-semibold ${pctColor(fcPct(form))}`}>{fcPct(form).toFixed(1)}%</span>
              <span className="text-muted-foreground">Prezzo consigliato (fc 30%)</span><span className="text-right font-medium text-primary">{fmt(suggested)}</span>
            </div>
          </div>
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="save-foodcost-button">Salva</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
